import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';
import LanguageSwitcher from './LanguageSwitcher';
import { getSlug } from '../utils/slugs';

const { FiMenu, FiX } = FiIcons;
const { FaCookieBite } = FaIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleNavClick = (id) => {
    setIsMenuOpen(false);

    const currentLang = i18n.language || 'en';
    const slug = getSlug(currentLang, id);
    let newPath = currentLang === 'en' ? `/${slug}` : `/${currentLang}/${slug}`;
    
    if (window.location.pathname !== newPath) {
      window.history.pushState(null, '', newPath);
    }

    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (id === 'home') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMenuOpen) setIsMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen]);

  const langPrefix = i18n.language === 'en' ? '' : `/${i18n.language}`;
  const featuresHref = `${langPrefix}/${getSlug(i18n.language || 'en', 'features')}`;
  const howItWorksHref = `${langPrefix}/${getSlug(i18n.language || 'en', 'how-it-works')}`;
  const pricingHref = `${langPrefix}/${getSlug(i18n.language || 'en', 'pricing')}`;

  return (
    <motion.header
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="bg-white shadow-sm sticky top-0 z-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div 
            className="flex items-center cursor-pointer" 
            onClick={(e) => { e.preventDefault(); handleNavClick('home'); }}
          >
            <SafeIcon icon={FaCookieBite} className="text-3xl text-amber-600 mr-2" />
            <span className="text-2xl font-bold text-gray-900">CookiePal</span>
          </div>

          <nav className="hidden md:flex items-center space-x-8">
            <a
              href={featuresHref}
              onClick={(e) => { e.preventDefault(); handleNavClick('features'); }}
              className="text-gray-600 hover:text-blue-500 transition-colors"
            >
              {t('header.features', { defaultValue: 'Features' })}
            </a>
            <a
              href={pricingHref}
              onClick={(e) => { e.preventDefault(); handleNavClick('pricing'); }}
              className="text-gray-600 hover:text-blue-500 transition-colors"
            >
              {t('header.pricing', { defaultValue: 'Pricing' })}
            </a>
            <a
              href={howItWorksHref}
              onClick={(e) => { e.preventDefault(); handleNavClick('how-it-works'); }}
              className="text-gray-600 hover:text-blue-500 transition-colors"
            >
              {t('header.howItWorks', { defaultValue: 'How it works' })}
            </a>
            <LanguageSwitcher />
          </nav>

          <div className="hidden md:flex items-center space-x-4">
            <a href="https://rebrand.ly/cookiepal-sign-5a33b3" rel="nofollow noopener noreferrer" className="text-gray-600 hover:text-blue-500 transition-colors">
              {t('header.login', { defaultValue: 'Login' })}
            </a>
            <a href="https://rebrand.ly/cookiepal-sign-5a33b3" rel="nofollow noopener noreferrer" className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors">
              {t('header.tryForFree', { defaultValue: 'Try for free' })}
            </a>
          </div>

          <div className="md:hidden flex items-center space-x-2">
            <LanguageSwitcher />
            <button onClick={toggleMenu} className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none">
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="text-2xl" />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white border-t border-gray-200"
          >
            <div className="px-4 py-3 space-y-3">
              <a
                href={featuresHref}
                onClick={(e) => { e.preventDefault(); handleNavClick('features'); }}
                className="block py-2 px-3 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-blue-500 rounded-md"
              >
                {t('header.features', { defaultValue: 'Features' })}
              </a>
              <a
                href={pricingHref}
                onClick={(e) => { e.preventDefault(); handleNavClick('pricing'); }}
                className="block py-2 px-3 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-blue-500 rounded-md"
              >
                {t('header.pricing', { defaultValue: 'Pricing' })}
              </a>
              <a
                href={howItWorksHref}
                onClick={(e) => { e.preventDefault(); handleNavClick('how-it-works'); }}
                className="block py-2 px-3 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-blue-500 rounded-md"
              >
                {t('header.howItWorks', { defaultValue: 'How it works' })}
              </a>
              <div className="pt-4 pb-1 border-t border-gray-200">
                <a href="https://rebrand.ly/cookiepal-sign-5a33b3" rel="nofollow noopener noreferrer" className="block w-full text-center py-2 px-4 mb-3 text-base font-medium text-gray-600 hover:text-blue-500 transition-colors">
                  {t('header.login', { defaultValue: 'Login' })}
                </a>
                <a href="https://rebrand.ly/cookiepal-sign-5a33b3" rel="nofollow noopener noreferrer" className="block w-full text-center py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors">
                  {t('header.tryForFree', { defaultValue: 'Try for free' })}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;